import HttpGlobal from "../Global/HttpGlobal.class";
import { customerApiBaseUrl, isNativeMode } from "../../../native/nativeMode";
import {
  BeneficiaryDataI,
  BeneficiariesAllI,
  BeneficiariesAvailableCapitalI,
  BeneficiariesBeneficiaryFormI,
  GetBeneficiaryInfoI,
} from "./Interfaces/BeneficiariesInterfaces";

export default class HttpBeneficiariesRepository extends HttpGlobal {
  private readonly API_BENEFICIARIES_URL:
    | string
    | undefined = `${process.env.REACT_APP_CUSTOMER_APP_URL}api/beneficiaries`;

  // Born-native: el cliente IT escribe/lee beneficiarios contra el gateway
  // (bloque B en ms-agl). Las rutas native cuelgan de /customer/leads/{id}/ y no
  // repiten el leadId al final.
  private beneficiariesBase = (): string =>
    isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/beneficiaries`
      : `${this.API_BENEFICIARIES_URL}`;

  getTypeBeneficiaries = async (): Promise<any> => {
    const url = isNativeMode()
      ? `${this.beneficiariesBase()}/type`
      : `${this.API_BENEFICIARIES_URL}/type/${this.leadId}`;
    const response = await this.generalGet(url);

    return response;
  };

  saveTypeBeneficiary = async (type: string): Promise<any> => {
    const url = isNativeMode()
      ? `${this.beneficiariesBase()}/type`
      : `${this.API_BENEFICIARIES_URL}/type/${this.leadId}`;
    const response = await this.generalPost({
      url,
      dataRequest: { type: type },
    });

    return response;
  };

  setBeneficiary = async (
    form: BeneficiariesBeneficiaryFormI,
    method: "POST" | "PUT" = "POST"
  ): Promise<any> => {
    let response = null;
    const url = isNativeMode()
      ? this.beneficiariesBase()
      : `${this.API_BENEFICIARIES_URL}/${this.leadId}`;

    if (method === "POST") {
      response = await this.generalPost({
        url,
        dataRequest: { ...form },
      });
    }

    if (method === "PUT") {
      response = await this.generalPut({
        url,
        dataRequest: { ...form },
      });
    }

    return response;
  };

  confirmBeneficiaries = async (accept: string): Promise<any> => {
    const url = isNativeMode()
      ? `${this.beneficiariesBase()}/confirm`
      : `${this.API_BENEFICIARIES_URL}/confirm/${this.leadId}`;
    const response = await this.generalPost({
      url,
      dataRequest: { acceptUserCommunications: accept },
    });

    return response;
  };

  deleteBeneficiary = async (beneficiaryId: string) => {
    const url = isNativeMode()
      ? `${this.beneficiariesBase()}/${beneficiaryId}`
      : `${this.API_BENEFICIARIES_URL}/${beneficiaryId}/${this.leadId}`;
    const response = await this.generalDelete({ url });

    return response;
  };

  getBeneficiaryInfo = async (
    beneficiaryId: string
  ): Promise<GetBeneficiaryInfoI> => {
    const url = isNativeMode()
      ? `${this.beneficiariesBase()}/info/${beneficiaryId}`
      : `${this.API_BENEFICIARIES_URL}/info/${beneficiaryId}/${this.leadId}`;
    const response = await this.generalGet(url);

    return response;
  };

  getAllResumeBeneficiaries = async (): Promise<BeneficiaryDataI[]> => {
    // En native el monolito lo neutraliza el choke-point ({}), y la posición
    // global hacía {}.map → TypeError (Free designation). El gateway ya da la
    // lista con nombre, % y capital en /beneficiaries/all.
    if (isNativeMode()) {
      const response = await this.generalGet(`${this.beneficiariesBase()}/all`);
      const items: any[] = Array.isArray(response?.beneficiaries) ? response.beneficiaries : [];
      return items.map((b) => ({
        codiceFiscale: "",
        fullName: String(b.fullName ?? ""),
        percentage: Number(b.percentage ?? 0),
        capital: Number(b.capital ?? 0),
        relation: "",
      }));
    }
    const response = await this.generalGet(
      `${this.API_BENEFICIARIES_URL}/resume/all/${this.leadId}`
    );
    return response;
  };

  getBeneficiariesAvailableCapital =
    async (): Promise<BeneficiariesAvailableCapitalI> => {
      // 15-oct-2026: en native la URL del monolito la neutraliza el choke-point
      // (resuelve {}) y rompía la pantalla (Free designation). El gateway calcula
      // lo mismo con lo que ya tiene de ms-agl.
      const url = isNativeMode()
        ? `${this.beneficiariesBase()}/availableCapital`
        : `${this.API_BENEFICIARIES_URL}/availableCapital/${this.leadId}`;
      const response = await this.generalGet(url);
      return response;
    };

  getBeneficiaryAvailableCapital = async (
    beneficiaryId: string
  ): Promise<BeneficiariesAvailableCapitalI> => {
    const url = isNativeMode()
      ? `${this.beneficiariesBase()}/availableCapital/${beneficiaryId}`
      : `${this.API_BENEFICIARIES_URL}/availableCapital/${this.leadId}/${beneficiaryId}`;
    const response = await this.generalGet(url);
    return response;
  };

  getAllBeneficiaries = async (): Promise<BeneficiariesAllI> => {
    const url = isNativeMode()
      ? `${this.beneficiariesBase()}/all`
      : `${this.API_BENEFICIARIES_URL}/all/${this.leadId}`;
    const response = await this.generalGet(url);
    return response;
  };
}
